import { useAuthContext } from '@myphx-engineering/auth/client';
import { FeatureFlagProvider } from '@myphx-engineering/feature-flags/client';
import { TelemetryProvider } from '@myphx-engineering/instrumentation/react';
import {
    AutoScrolltop,
    NavBarProvider,
} from '@myphx-engineering/phx-design-system';

import { flagProvider } from '../..';
import { AppAuthProvider } from '../../contexts/AppAuthProvider';
import { useAppConfig } from '../../contexts/AppConfigProvider';
import { ErrorSessionProvider } from '../../contexts/ErrorSessionProvider';
import { GraphQLProvider } from '../../contexts/GraphQLProvider';
import { PatientProvider } from '../../contexts/PatientProvider';
import { SidebarProvider } from '../../contexts/SidebarProvider';
import { useAppSystemDegradationStatus } from '../../hooks/use-app-system-degradation-status';
import { useFlags } from '../../hooks/use-flags';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { Maintenance } from '../maintenance/Maintenance';
import RootErrorRoute from '../router/RootErrorRoute';

import { AppContent } from './AppContent';
import { AppSidebar } from './AppSidebar';

const AppShellContent = () => {
    const { isAuthenticated } = useAuthContext();
    const { isActive, disableAuthFlows } = useAppSystemDegradationStatus();

    if (isActive && disableAuthFlows && isAuthenticated) {
        return <Maintenance />;
    }

    return (
        <>
            <AppContent />
            <AppSidebar />
        </>
    );
};

const RenderApp = () => {
    const { enableLillyDirect } = useFlags();
    const { authentication } = useAppConfig();

    return (
        enableLillyDirect && (
            <AppAuthProvider {...authentication}>
                <GraphQLProvider>
                    <PatientProvider>
                        <NavBarProvider>
                            <SidebarProvider>
                                <AppShellContent />
                            </SidebarProvider>
                        </NavBarProvider>
                    </PatientProvider>
                </GraphQLProvider>
            </AppAuthProvider>
        )
    );
};

const AppRoot = () => {
    const { telemetry } = useAppConfig();

    return (
        <ErrorSessionProvider>
            <ErrorBoundary fallback={<RootErrorRoute p="md" />}>
                <TelemetryProvider {...telemetry}>
                    <FeatureFlagProvider provider={flagProvider}>
                        <RenderApp />
                    </FeatureFlagProvider>
                </TelemetryProvider>
                <AutoScrolltop />
            </ErrorBoundary>
        </ErrorSessionProvider>
    );
};

export default AppRoot;
